/**
 * SA-1387 (spec §5.2): the mailbox list column. One button per mailbox, its
 * accessible name the display name alone (a long name truncates on screen
 * only), the status chip and one line naming what needs attention, else
 * what the mailbox does. Grouped by channel only when both are present.
 */
import { useId } from 'react'
import { tourAnchor } from '@/components/tour/tourAnchor'
import { Badge } from '@/components/ui'
import { cn } from '@/utils'
import type { Mailbox } from '@/services/mailboxes'
import type { AttentionLine, MailboxGroup } from './mailboxListModel'
import { LIST_LABEL, NEW_MAILBOX, NOT_SAVED_YET, STATUS_BADGE } from './mailboxesText'

interface MailboxListProps {
  groups: MailboxGroup[]
  selectedId: string | null
  /** The New mailbox form is in the pane: a first row, not saved yet, shows selected. */
  creating: boolean
  lineFor: (mb: Mailbox) => AttentionLine
  onSelect: (id: string) => void
}

const ROW = 'block w-full border-l-2 px-3 py-2 text-left'
const SELECTED = 'bg-accent border-l-primary'
const TONE: Record<AttentionLine['tone'], string> = {
  destructive: 'text-destructive',
  warning: 'text-warning-strong',
  muted: 'text-muted-foreground',
}

export function MailboxList({ groups, selectedId, creating, lineFor, onSelect }: MailboxListProps) {
  return (
    <nav
      aria-label={LIST_LABEL}
      {...tourAnchor('mailboxes.list')}
      className="overflow-y-auto rounded-lg border border-section-border bg-card py-1 shadow-xs @[40rem]:sticky @[40rem]:top-0 @[40rem]:max-h-[calc(100dvh-8rem)] @[40rem]:self-start"
    >
      {creating && (
        <div aria-current="true" className={cn(ROW, SELECTED)}>
          <span className="block truncate text-sm font-medium text-foreground">{NEW_MAILBOX}</span>
          <span className="block truncate text-xs text-muted-foreground">{NOT_SAVED_YET}</span>
        </div>
      )}
      {groups.map((group) => (
        <ListGroup key={group.channel} group={group} selectedId={selectedId} lineFor={lineFor} onSelect={onSelect} />
      ))}
    </nav>
  )
}

function ListGroup({
  group, selectedId, lineFor, onSelect,
}: Omit<MailboxListProps, 'groups' | 'creating'> & { group: MailboxGroup }) {
  const headingId = useId()
  const rows = group.mailboxes.map((mb) => {
    const selected = mb.id === selectedId
    const badge = STATUS_BADGE[mb.status]
    const line = lineFor(mb)
    return (
      <button
        key={mb.id}
        type="button"
        data-testid="mailbox-row"
        aria-label={mb.display_name}
        aria-describedby={`${headingId}-${mb.id}-status ${headingId}-${mb.id}-line`}
        aria-current={selected ? 'true' : undefined}
        onClick={() => onSelect(mb.id)}
        className={cn(ROW, 'hover:bg-accent', selected ? SELECTED : 'border-l-transparent')}
      >
        <span className="flex min-w-0 items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">{mb.display_name}</span>
          <Badge id={`${headingId}-${mb.id}-status`} variant={badge.variant} className="shrink-0">{badge.label}</Badge>
        </span>
        <span id={`${headingId}-${mb.id}-line`} className={cn('mt-0.5 block truncate text-xs', TONE[line.tone])}>{line.text}</span>
      </button>
    )
  })
  if (group.heading === null) return <>{rows}</>
  return (
    <div role="group" aria-labelledby={headingId} className="pb-1">
      <div id={headingId} className="px-3 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {group.heading}
      </div>
      {rows}
    </div>
  )
}
